/**
 * Lazy singleton accessors for the Firebase app and Auth instances used by
 * Firebase-backed login.
 *
 * Initialization is deferred until first call so a deployment that doesn't use
 * Firebase auth never spins up a Firebase app. Configuration comes from the
 * active deployment via `getDeploymentConfig()`; when `emulateFirebase` is set,
 * Auth is wired to the local emulator port from
 * `EXPO_PUBLIC_AUTH_EMULATOR_PORT`. Authentication is the client's only use of
 * Firebase; all data goes through the server's oRPC API.
 */

import type { FirebaseApp } from 'firebase/app';
import { initializeApp } from 'firebase/app';
import type { Auth } from 'firebase/auth';
import { connectAuthEmulator, getAuth } from 'firebase/auth';
import { getDeploymentConfig } from 'util/util';

let app: FirebaseApp | null = null;
let auth: Auth | null = null;

const deploymentConfig = getDeploymentConfig();
const firebaseConfig = deploymentConfig.firebaseConfig;

/**
 * Get or initialize the Firebase app. This is lazily initialized on first call.
 */
function getFirebaseApp(): FirebaseApp {
    if (!app) {
        if (!firebaseConfig) {
            throw new Error('Firebase is not configured for this deployment');
        }
        app = initializeApp(firebaseConfig);
    }
    return app;
}

export function getFirebaseAuth(): Auth {
    if (!auth) {
        const firebaseApp = getFirebaseApp();
        auth = getAuth(firebaseApp);

        // Connect to emulator if configured
        if (deploymentConfig.emulateFirebase) {
            connectAuthEmulator(auth, `http://localhost:${process.env.EXPO_PUBLIC_AUTH_EMULATOR_PORT || '9099'}`, {
                // The application does not support sign in with passwords.
                // Therefore, do not warn developers not to type real passwords.
                // The warning is not helpful and the banner gets in the way.
                disableWarnings: true,
            });
        }
    }
    return auth;
}
